import { JSX } from "react";
import { Navigate } from "react-router-dom";
import { AboutUsPage } from "./AboutUs/AboutUsPage";
import { CommunityPage } from "./Community/CommunityPage";
import { HomePage } from "./Home/HomePage";
import { LoginPage } from "./Login/LoginPage";
import { MatchPage } from "./Match/MatchPage";
import { MatchesPage } from "./Matches/MatchesPage";
import { PickemPage } from "./Pickem/PickemPage";
import { PickemCreateGroupPage } from "./PickemCreateGroup/PickemCreateGroupPage";
import { PickemGroupPage } from "./PickemGroup/PickemGroupPage";
import { PickemJoinGroupPage } from "./PickemJoinGroup/PickemJoinGroupPage";
import { PickemMakePicksPage } from "./PickemMakePicks/PickemMakePicksPage";
import { PickemUserPage } from "./PickemUser/PickemUserPage";
import { PrivacyPage } from "./Privacy/PrivacyPage";
import { TermsOfServicePage } from "./TermsOfService/TermsOfServicePage";
import { TourPage } from "./Tour/TourPage";
import { TournamentPage } from "./Tournament/TournamentPage";
import { TournamentsPage } from "./Tournaments/TournamentsPage";
import { TrackPage } from "./Track/TrackPage";
import { TracksPage } from "./Tracks/TracksPage";

export enum PageRestriction {
  NOT_LOGGED_IN,
  LOGGED_IN,
}

export type PageConfig = {
  path: string;
  page: JSX.Element;
  title?: string;
  description?: string;
  showNavBarAndFooter?: boolean;
  restriction?: PageRestriction;
};

export const aboutUsPath = "/about-us";
export const communityPath = "/community";
export const homePath = "/";
export const loginPath = "/login";
export const matchesPath = "/matches";
export const matchPath = `${matchesPath}/:id`;
export const pickemPath = "/pickem";
export const pickemCreateGroupPath = `${pickemPath}/create-group`;
export const pickemJoinGroupPath = `${pickemPath}/join-group`;
export const pickemGroupPath = `${pickemPath}/group`;
export const pickemGroupPathWithParams = `${pickemGroupPath}/:groupId`;
export const pickemMakePicksPath = `${pickemPath}/make-picks`;
export const pickemMakePicksPathWithParams = `${pickemMakePicksPath}/:tournamentId/:pickemId`;
export const pickemUserPath = `${pickemPath}/user`;
export const pickemUserPathWithParams = `${pickemUserPath}/:uid`;
export const privacyPath = "/privacy";
export const termsOfServicePath = "/terms-of-service";
export const toursPath = "/tours";
export const tourPath = `${toursPath}/:id`;
export const tourPathWithTab = `${tourPath}/:tabName`;
export const tournamentsPath = "/tournaments";
export const tournamentPath = `${tournamentsPath}/:id`;
export const tournamentPathWithTab = `${tournamentPath}/:tabName`;
export const tracksPath = "/tracks";
export const trackPath = `${tracksPath}/:id`;

export const ambitPath = "/ambit";

export const buildMatchPath = (matchId: string) => {
  return `${matchesPath}/${matchId}`;
};

export const buildPickemGroupPath = (groupId: string) => {
  return `${pickemGroupPath}/${groupId}`;
};

export const buildPickemMakePicksPath = (
  tournamentId: string,
  pickemId: string
) => {
  return `${pickemMakePicksPath}/${tournamentId}/${pickemId}`;
};

export const buildTourPath = (tourId: string, tabName?: string) => {
  return `${toursPath}/${tourId}${!!tabName ? `/${tabName}` : ""}`;
};

export const buildTournamentPath = (tournamentId: string, tabName?: string) => {
  return `${tournamentsPath}/${tournamentId}${!!tabName ? `/${tabName}` : ""}`;
};

export const buildTrackPath = (trackId: string) => {
  return `${tracksPath}/${trackId}`;
};

export const PAGE_CONFIGS: PageConfig[] = [
  {
    path: aboutUsPath,
    page: <AboutUsPage />,
    showNavBarAndFooter: true,
    title: "About Us - eCircuitMania",
    description:
      "Learn about the creators of eCircuitMania and the mission to provide the best TrackMania eSports experience.",
  },
  {
    path: communityPath,
    page: <CommunityPage />,
    showNavBarAndFooter: true,
    title: "Community - eCircuitMania",
    description:
      "Join TrackMania communities to connect with other players and discuss TrackMania on eCircuitMania.",
  },
  {
    path: homePath,
    page: <HomePage />,
    showNavBarAndFooter: true,
    title: "eCircuitMania - TrackMania eSports Hub",
    description:
      "Track the latest TrackMania eSports with eCircuitMania. Get real-time rankings, tournament results, statistics, and make predictions.",
  },
  {
    path: loginPath,
    page: <LoginPage />,
    showNavBarAndFooter: false,
    restriction: PageRestriction.NOT_LOGGED_IN,
    title: "Login - eCircuitMania",
    description:
      "Login with your username for access to full features on eCircuitMania.",
  },
  {
    path: matchesPath,
    page: <MatchesPage />,
    showNavBarAndFooter: true,
    title: "Matches - eCircuitMania",
    description:
      "View match results and browse detailed TrackMania stats for in-depth analysis on eCircuitMania.",
  },
  {
    path: matchPath,
    page: <MatchPage />,
    showNavBarAndFooter: true,
    title: "Match Details - eCircuitMania",
    description:
      "Analyze in-depth statistics of a Trackmania match on eCircuitMania.",
  },
  {
    path: pickemPath,
    page: <PickemPage />,
    showNavBarAndFooter: true,
    title: "Pick'em Predictions - eCircuitMania",
    description:
      "Test your TrackMania knowledge by predicting matches and tournament outcomes, and see how you stack up against others with Pick'em on eCircuitMania.",
  },
  {
    path: pickemCreateGroupPath,
    page: <PickemCreateGroupPage />,
    showNavBarAndFooter: true,
    restriction: PageRestriction.LOGGED_IN,
    title: "Pick'em Create Group - eCircuitMania",
    description:
      "Create a community group for predictions of TrackMania tournaments to be compared on eCircuitMania.",
  },
  {
    path: pickemGroupPathWithParams,
    page: <PickemGroupPage />,
    showNavBarAndFooter: true,
    restriction: PageRestriction.LOGGED_IN,
    title: "Pick'em Group - eCircuitMania",
    description:
      "Compare prediction results for your TrackMania community on eCircuitMania.",
  },
  {
    path: pickemJoinGroupPath,
    page: <PickemJoinGroupPage />,
    showNavBarAndFooter: true,
    restriction: PageRestriction.LOGGED_IN,
    title: "Pick'em Join Group - eCircuitMania",
    description:
      "Join a TrackMania community group to compare prediction results on eCircuitMania.",
  },
  {
    path: pickemMakePicksPathWithParams,
    page: <PickemMakePicksPage />,
    showNavBarAndFooter: true,
    restriction: PageRestriction.LOGGED_IN,
    title: "Pick'em Predictions - eCircuitMania",
    description:
      "Make predictions of TrackMania tournament and match outcomes on eCircuitMania.",
  },
  {
    path: pickemUserPathWithParams,
    page: <PickemUserPage />,
    showNavBarAndFooter: true,
    title: "Pick'em User - eCircuitMania",
    description:
      "See your predictions of TrackMania tournament and match outcomes on eCircuitMania.",
  },
  {
    path: privacyPath,
    page: <PrivacyPage />,
    showNavBarAndFooter: true,
    title: "Privacy Policy - eCircuitMania",
    description: "Read the Privacy Policy of eCircuitMania.",
  },
  {
    path: termsOfServicePath,
    page: <TermsOfServicePage />,
    showNavBarAndFooter: true,
    title: "Terms of Service - eCircuitMania",
    description: "Read the Terms of Service of eCircuitMania.",
  },
  {
    path: tourPath,
    page: <TourPage />,
    showNavBarAndFooter: true,
    title: "Tour Details - eCircuitMania",
    description:
      "Stay informed on matches and rankings of TrackMania tours on eCircuitMania.",
  },
  {
    path: tourPathWithTab,
    page: <TourPage />,
    showNavBarAndFooter: true,
    title: "Tour Details - eCircuitMania",
    description:
      "Stay informed on matches and rankings of TrackMania tours on eCircuitMania.",
  },
  {
    path: tournamentPath,
    page: <TournamentPage />,
    showNavBarAndFooter: true,
    title: "Tournament Details - eCircuitMania",
    description:
      "Stay informed on matches and rankings of TrackMania tournaments on eCircuitMania.",
  },
  {
    path: tournamentPathWithTab,
    page: <TournamentPage />,
    showNavBarAndFooter: true,
    title: "Tournament Details - eCircuitMania",
    description:
      "Stay informed on matches and rankings of TrackMania tournaments on eCircuitMania.",
  },
  {
    path: tournamentsPath,
    page: <TournamentsPage />,
    showNavBarAndFooter: true,
    title: "Tournaments - eCircuitMania",
    description:
      "Follow active events, explore past competitions, and access specialized tournament sections for detailed stats of TrackMania eSports on eCircuitMania.",
  },
  {
    path: trackPath,
    page: <TrackPage />,
    showNavBarAndFooter: true,
    title: "Track Details - eCircuitMania",
    description:
      "Analyze statistics and top racetimes of a track played in TrackMania tournaments on eCircuitMania.",
  },
  {
    path: tracksPath,
    page: <TracksPage />,
    showNavBarAndFooter: true,
    title: "Tracks - eCircuitMania",
    description:
      "Explore the tracks played in TrackMania tournaments and analyze stats on focused track views for detailed insights on eCircuitMania.",
  },

  // Vanity URLs
  {
    path: ambitPath,
    page: <Navigate replace to={buildTourPath("Ambit2025", "Overview")} />,
  },
];
